import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import type { QuizQuestion, QuestionOption } from "@/types/quiz";

interface AddQuestionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (question: QuizQuestion) => void;
  existingKeys: string[];
  editQuestion?: QuizQuestion;
  nextDisplayOrder: number;
}

interface OptionDraft {
  id: string;
  text: string;
  value: string;
}

const QUESTION_KEY_REGEX = /^[a-z][a-z0-9_]*$/;

function generateId(): string {
  return crypto.randomUUID();
}

function createEmptyOption(): OptionDraft {
  return { id: generateId(), text: "", value: "" };
}

export function AddQuestionDialog({
  open,
  onClose,
  onSubmit,
  existingKeys,
  editQuestion,
  nextDisplayOrder,
}: AddQuestionDialogProps) {
  const isEdit = !!editQuestion;

  const [questionKey, setQuestionKey] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] =
    useState<QuizQuestion["questionType"]>("single-choice");
  const [isOptional, setIsOptional] = useState(false);
  const [options, setOptions] = useState<OptionDraft[]>([
    createEmptyOption(),
    createEmptyOption(),
  ]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (editQuestion) {
      setQuestionKey(editQuestion.questionKey);
      setQuestionText(editQuestion.questionText);
      setQuestionType(editQuestion.questionType);
      setIsOptional(editQuestion.isOptional ?? false);
      setOptions(
        editQuestion.options && editQuestion.options.length > 0
          ? editQuestion.options.map((o) => ({
              id: o.id,
              text: o.text,
              value: o.value,
            }))
          : [createEmptyOption(), createEmptyOption()],
      );
    } else {
      setQuestionKey("");
      setQuestionText("");
      setQuestionType("single-choice");
      setIsOptional(false);
      setOptions([createEmptyOption(), createEmptyOption()]);
    }
  }, [open, editQuestion]);

  if (!open) return null;

  const needsOptions =
    questionType === "single-choice" || questionType === "multiple-choice";

  function validate(): string | null {
    const trimmedKey = questionKey.trim();
    if (!trimmedKey) return "Frage-Key ist ein Pflichtfeld.";
    if (!QUESTION_KEY_REGEX.test(trimmedKey))
      return "Frage-Key muss mit einem Kleinbuchstaben beginnen und darf nur Kleinbuchstaben, Ziffern und Unterstriche enthalten.";
    if (!isEdit && existingKeys.includes(trimmedKey))
      return "Dieser Frage-Key wird bereits verwendet.";
    if (!questionText.trim()) return "Fragetext ist ein Pflichtfeld.";
    if (needsOptions) {
      if (options.length < 2)
        return "Mindestens 2 Antwortoptionen sind erforderlich.";
      for (let i = 0; i < options.length; i++) {
        if (!options[i].text.trim())
          return `Option ${i + 1}: Text darf nicht leer sein.`;
        if (!options[i].value.trim())
          return `Option ${i + 1}: Wert darf nicht leer sein.`;
      }
    }
    return null;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const builtOptions: QuestionOption[] = needsOptions
      ? options.map((o) => ({
          id: o.id,
          text: o.text.trim(),
          value: o.value.trim(),
        }))
      : [];

    const question: QuizQuestion = {
      questionKey: questionKey.trim(),
      questionText: questionText.trim(),
      questionType,
      displayOrder: editQuestion ? editQuestion.displayOrder : nextDisplayOrder,
      isOptional,
      options: builtOptions,
    };

    onSubmit(question);
  }

  function updateOption(index: number, field: "text" | "value", val: string) {
    setOptions((prev) =>
      prev.map((o, i) => (i === index ? { ...o, [field]: val } : o)),
    );
  }

  function removeOption(index: number) {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, i) => i !== index));
  }

  function addOption() {
    setOptions((prev) => [...prev, createEmptyOption()]);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
        role="button"
        tabIndex={-1}
        aria-label="Dialog schließen"
      />
      <div className="relative z-50 mx-4 max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg border bg-background p-6 shadow-lg">
        <h2 className="text-lg font-semibold">
          {isEdit ? "Frage bearbeiten" : "Frage hinzufügen"}
        </h2>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="question-key">Frage-Key *</Label>
            <Input
              id="question-key"
              value={questionKey}
              onChange={(e) => setQuestionKey(e.target.value)}
              placeholder="z.B. hauttyp oder budget"
              disabled={isEdit}
              required
            />
            <p className="text-xs text-muted-foreground">
              Eindeutiger Schlüssel (Kleinbuchstaben, Ziffern, Unterstriche)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="question-text">Fragetext *</Label>
            <Input
              id="question-text"
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="z.B. Wie würdest du deinen Hauttyp beschreiben?"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Fragetyp *</Label>
            <Select
              value={questionType}
              onValueChange={(v) =>
                setQuestionType(v as QuizQuestion["questionType"])
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single-choice">Single-Choice</SelectItem>
                <SelectItem value="multiple-choice">Multiple-Choice</SelectItem>
                <SelectItem value="text-input">Texteingabe</SelectItem>
                <SelectItem value="range-slider">Schieberegler</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="question-optional"
              checked={isOptional}
              onChange={(e) => setIsOptional(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            <Label htmlFor="question-optional" className="cursor-pointer">
              Optionale Frage
            </Label>
          </div>

          {needsOptions && (
            <div className="space-y-3">
              <Label>Antwortoptionen *</Label>
              {options.map((opt, index) => (
                <div key={opt.id} className="flex items-start gap-2">
                  <div className="flex flex-1 gap-2">
                    <Input
                      value={opt.text}
                      onChange={(e) =>
                        updateOption(index, "text", e.target.value)
                      }
                      placeholder="Text"
                      className="flex-1"
                    />
                    <Input
                      value={opt.value}
                      onChange={(e) =>
                        updateOption(index, "value", e.target.value)
                      }
                      placeholder="Wert"
                      className="w-32"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeOption(index)}
                    disabled={options.length <= 2}
                    className="shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addOption}
                className="gap-1"
              >
                <Plus className="h-3.5 w-3.5" /> Option hinzufügen
              </Button>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit">
              {isEdit ? "Übernehmen" : "Frage hinzufügen"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
